import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import TemplateForm from "@pages/Template/components/TemplateForm";
import SubTitleHeader from "@shared/components/SubTitleHeader";
import useHandleSaveTemplate from "@pages/Template/utils/useHandleSaveTemplate";
import FileUpload from "@pages/Template/components/FileUpload";
import TemplateGeneration from "@pages/Template/components/TemplateGeneration";
import { useForm, FormProvider } from "react-hook-form";
import { useUnsavedChangesWarning } from "@shared/components/UnsavedChangesPopup/useUnsavedChangesWarning";
import UnsavedChangesModal from "@shared/components/UnsavedChangesPopup/UnsavedChangesModal";
import { useTemplate } from "@utils/useTemplate";
import { TemplateFormData } from "@shared/interfaces/Template";
import Loader from "@shared/components/Loader";

const EditTemplatePage: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const location = useLocation();
  const { reportName, projectId, checkedItems, isChoosing, returnUrl } =
    location.state || {};
  const [isTemplateGenerating, setIsTemplateGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isGenerated, setIsGenerated] = useState(false);
  const [taskId, setTaskId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { getTemplateById } = useTemplate();

  const [submitting, setSubmitting] = useState(false);

  const methods = useForm({
    mode: "all", // ✅ Show errors immediately when the user types
    reValidateMode: "onChange",
    criteriaMode: "all",
    defaultValues: {
      templateName: "",
      templateDescription: "",
      templatePrompt: "",
      templateType: "",
    },
  });

  const {
    setValue,
    reset,
    getValues,
    handleSubmit,
    formState: { isValid },
  } = methods;
  const { watch } = methods;
  const templatePrompt = watch("templatePrompt");
  watch("templateName");
  watch("templateDescription");

  const handleDiscardChanges = () => {
    fetchTemplate(); // reset to original data
    setIsEditing(false);
  };

  const handleClosePreview = () => {
    navigate("/templates");
  };

  useEffect(() => {
    if (!templatePrompt) {
      setIsGenerated(false);
    }
  }, [templatePrompt]);
  const fetchTemplate = async () => {
    if (!templateId) return;

    try {
      const data = await getTemplateById(templateId);
      if (data) {
        reset({
          templateName: data.name,
          templateDescription: data.description,
          templatePrompt: data.prompt,
          templateType: data.template_type,
        });
      }
    } catch (error) {
      console.error("Error fetching template:", error);
    }
  };
  useEffect(() => {
    fetchTemplate();
  }, [templateId]);

  const handleSaveTemplate = useHandleSaveTemplate(
    {
      templateId,
      isChoosing,
      projectId,
      reportName,
      checkedItems,
      returnUrl,
    },
    methods
  );

  const onSave = (values: TemplateFormData) => {
    if (!isValid) return;

    handleSaveTemplate(
      {
        name: values.templateName,
        description: values.templateDescription,
        prompt: values.templatePrompt,
        template_type: values.templateType == "" ? null : values.templateType,
      },
      setSubmitting
    );

    setIsEditing(false);
  };

  const { handleProceed, handleCancel, isBlocked } = useUnsavedChangesWarning(
    getValues(), // current form values
    methods.control._defaultValues // initial/default values
  );

  return (
    <FormProvider {...methods}>
      <UnsavedChangesModal
        onProceed={handleProceed}
        onCancel={handleCancel}
        isOpen={isBlocked}
      />
      <form onSubmit={handleSubmit(onSave)}>
        <div className="flex flex-col h-[calc(100vh-78px)] relative">
          <div className="flex flex-1 px-8">
            <div className="flex-1 flex flex-col">
              <SubTitleHeader
                title={isEditing ? "Edit Template" : "Template Preview"}
                spacer={true}
              >
                {isEditing ? (
                  <div>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDiscardChanges();
                      }}
                      className="py-2 px-4 rounded-md text-sm 2xl:text-base font-medium text-blue-700 hover:text-blue-600 transition relative mr-4"
                    >
                      Discard
                    </button>
                    <button
                      type="submit"
                      className="py-2 px-4 rounded-md text-sm 2xl:text-base text-white bg-blue-700 hover:bg-blue-600 transition relative"
                    >
                      {submitting ? (
                        <>
                          <Loader color="#fff" />
                        </>
                      ) : (
                        <span>Save Changes</span>
                      )}
                    </button>
                  </div>
                ) : (
                  <div>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsEditing(true);
                      }}
                      className="py-2 px-4 text-sm 2xl:text-base rounded-md font-medium text-blue-700 hover:text-blue-600 transition relative mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={handleClosePreview}
                      className="py-2 px-4 text-sm 2xl:text-base rounded-md text-white bg-blue-700 hover:bg-blue-600 transition relative"
                    >
                      Close Preview
                    </button>
                  </div>
                )}
              </SubTitleHeader>
              <div className="flex flex-1 pt-5">
                <div className="flex-1">
                  <TemplateForm isEditing={isEditing} />
                </div>
                <div className="w-[30%] p-4 pt-0 pr-0 ml-6 mt-3">
                  <h2 className="mb-3 text-gray-700 text-sm 2xl:text-base">
                    Use a Previous Report
                  </h2>
                  <div className="h-[calc(100%-50px)] bg-gray-100 rounded-lg flex flex-col items-center justify-center">
                    {!isTemplateGenerating && (
                      <FileUpload
                        isEditing={isEditing}
                        onTemplateGenerated={setTaskId}
                        setIsTemplateGenerating={setIsTemplateGenerating}
                      />
                    )}
                    <TemplateGeneration
                      taskId={taskId}
                      isTemplateGenerating={isTemplateGenerating}
                      setIsTemplateGenerating={setIsTemplateGenerating}
                      setIsGenerated={setIsGenerated}
                      setTemplatePrompt={(prompt: string | null) =>
                        setValue("templatePrompt", prompt ?? "", {
                          shouldValidate: true,
                          shouldDirty: true,
                          shouldTouch: true,
                        })
                      }
                      setGenerationStatus={setGenerationStatus}
                      generationStatus={generationStatus}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default EditTemplatePage;
